import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormBuilder, UntypedFormArray, UntypedFormGroup} from "@angular/forms";
import {NgDialogAnimationService} from 'src/app/services/core/ng-dialog-animation.service';
import {ToasterService} from "../../../../services/core/toaster.service";
import {BackendApiService} from "../../../../services/backend-api.service";
import {
  TicketUserPopupComponent,
  ticketPeopleKey,
  ticketPeopleTypeLabel
} from "../../add-edit-ticket/ticket-user-popup/ticket-user-popup.component";
import {
  TicketChecklistItemsPopupComponent,
  checklistItemKindIcon,
  pruneChecklistItems,
  ticketItemKey,
  toChecklistItemRefs
} from "../../add-edit-ticket/ticket-checklist-items-popup/ticket-checklist-items-popup.component";

@Component({
    selector: 'app-ticket-checklists',
    templateUrl: './ticket-checklists.component.html',
    styleUrls: ['./ticket-checklists.component.scss'],
    standalone: false
})
export class TicketChecklistsComponent implements OnInit, OnChanges {
  public isImportant: boolean = false;
  public CONST_OBSERVER_TYPE: string = 'observer';
  public CONST_PARTICIPANT_TYPE: string = 'participant';
  @Output() callback = new EventEmitter();
  @Input() checkLists: any;
  @Input() model: any = {};
  @Input() ticketId?: number | string;
  @Input() isLocked: boolean = false;
  /** The ticket's Materials & Services line items — what a checklist item can link to. */
  @Input() ticketItems: any[] | null = null;
  /** Job tickets only, mirroring the Materials & Services tab. */
  @Input() showMaterials: boolean = false;

  readonly kindIcon = checklistItemKindIcon;
  readonly lineItemKey = ticketItemKey;

  public sectionForm: UntypedFormGroup = new UntypedFormGroup({
    checklists: new UntypedFormArray([]),
  })
  constructor(
    public dialog: NgDialogAnimationService,
    private fb: FormBuilder,
    public toasterService: ToasterService,
    public backendApiService: BackendApiService
  ) { }

  ngOnInit(): void {
    console.log('[CHECKLIST] Component initialized', {
      checkLists: this.checkLists,
      hasModel: !!this.model,
      ticketId: this.ticketId
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    console.log('[CHECKLIST] Changes detected', changes);
    // Only when model itself changed — any other input (isLocked, ticketItems…)
    // would otherwise wipe it to undefined.
    if (changes.model) {
      this.model = changes.model.currentValue;
    }
    if (changes.ticketItems) {
      this.pruneRemovedLineItems();
    }
    if(changes.checkLists?.currentValue?.length > 0) {
      if (this.suppressNextChecklistsRebuild) {
        // This [checkLists] update is just the parent echoing back the array we
        // ourselves emitted via getDataForParent() a moment ago — the FormArray
        // already reflects it. Rebuilding here would clear() and re-add every
        // row with edit_mode=false, snapping whichever row the user is actively
        // editing out of edit mode on every keystroke-level interaction
        // (toggling Important, adding an observer, checking a box).
        this.suppressNextChecklistsRebuild = false;
        return;
      }
      this.checklists().clear();
      this.checkLists.forEach((item: any) => {
        this.addChecklist(item,true,false);
      })
      console.log('[CHECKLIST] Loaded', this.checkLists.length, 'checklist items');
    }
  }

  openObserverParticipantPopup(event: any,type: any,index: any) {
    const field = type == this.CONST_OBSERVER_TYPE ? 'observers' : 'participants';
    const dialogRef = this.dialog.open(TicketUserPopupComponent,{
      width: '420px',
      height: '500px',
      data: {
        attachAbleUser: ['Users', 'Vendors', 'Customers', 'Subcontractors'],
        multiple: true,
        selected: this.checklists().at(index)?.get(field)?.value || [],
        type: type,
        index: index,
      }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.items?.length) {
        this.addUsers(result.index, field, result.items);
      }
    })
  }

  /**
   * A line item removed from Materials & Services is unlinked server-side
   * (TicketRepository::deleteTicketItem); drop it from the rows here as well so
   * the next checklist save doesn't try to re-link it.
   */
  private pruneRemovedLineItems() {
    let changed = false;
    this.checklists().controls.forEach(row => {
      const kept = pruneChecklistItems(row.get('items')?.value, this.ticketItems as any[]);
      if (kept) {
        row.get('items')?.setValue(kept);
        changed = true;
      }
    });
    if (changed) {
      this.getDataForParent();
    }
  }

  openLineItemsPopup(index: any) {
    const dialogRef = this.dialog.open(TicketChecklistItemsPopupComponent, {
      width: '600px',
      maxWidth: '95vw',
      // Top-anchored: the popup grows as the linked strip fills, and a centred
      // dialog would jump upward under the pointer each time.
      position: { top: '12vh' },
      data: {
        items: this.ticketItems || [],
        selected: this.checklists().at(index)?.get('items')?.value || [],
        index: index,
      }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result && Array.isArray(result.items)) {
        this.checklists().at(result.index)?.get('items')?.setValue(result.items);
        this.updateChecklistAtBackend(result.index);
      }
    });
  }

  removeLineItem(index: any, item: any) {
    const control = this.checklists().at(index)?.get('items');
    const key = ticketItemKey(item);
    control?.setValue((control.value || []).filter((row: any) => ticketItemKey(row) !== key));
    this.updateChecklistAtBackend(index);
  }

  /** Tooltip for the collapsed row's line-item count. */
  lineItemsSummary(index: any): string {
    return (this.checklists().at(index)?.get('items')?.value || [])
      .map((item: any) => `${item.item_name} (${item.quantity ?? '—'})`)
      .join(', ');
  }

  removeCheckList(index: any) {
    const id = this.checklists().at(index).get('id')?.value;
    this.checklists().removeAt(index);
    this.backendApiService.deleteApi('ticket/checklist/' + id).subscribe(
      (response: any) => {
        this.toasterService.showSuccessMessage(response?.data?.message);
        // IMPORTANT: Emit callback to parent so progress bar updates when checklist item removed
        this.getDataForParent();
      },
      () => {
        this.toasterService.showError('Error', 'Failed to delete checklist');
      }
    );
  }

  isImportantCheckBox(index: any) {
    const value = this.checklists().at(index).get('is_important')?.value;
    if(value == true) {
      this.checklists().at(index).get('is_important')?.setValue(false);
    } else {
      this.checklists().at(index).get('is_important')?.setValue(true);
    }
    // this.isImportant = !this.isImportant;
    this.updateChecklistAtBackend(index);
  }

  checklists(): UntypedFormArray {
    return this.sectionForm.get('checklists') as UntypedFormArray;
  }

  private getTicketId(): number | string | undefined {
    return this.model?.id ?? this.ticketId;
  }

  /** Appends picked people to a checklist row, skipping any already on it (same type + id), then saves. */
  addUsers(index: any, field: string, items: any[]) {
    const control = this.checklists().at(index)?.get(field);
    const current = control?.getRawValue() || [];
    const existing = new Set(current.map(ticketPeopleKey));
    const additions = items.filter((item: any) => !existing.has(ticketPeopleKey(item)));

    if (additions.length) {
      control?.setValue([...current, ...additions]);
      this.updateChecklistAtBackend(index);
    }
  }

  addChecklist(checklist: any = '',viewMode: boolean = false,editMode:boolean = true) {
    this.checklists().push(this.newChecklist(checklist,viewMode,editMode));
  }

  newChecklist(checklist: any = '',viewMode: boolean = false,editMode:boolean = true) {
    return this.fb.group({
      id: [checklist ? checklist?.id : ''],
      text: [checklist ? checklist?.text : 'text'],
      observers: [checklist ? checklist?.observers : []],
      participants: [checklist ? checklist?.participants : []],
      items: [checklist?.items || []],
      is_completed: [checklist ? checklist?.is_completed : false],
      is_important: [checklist ? checklist?.is_important : false],
      view_mode: [viewMode],
      edit_mode: [editMode],
    })
  }

  /** Set before emitting so ngOnChanges can recognize its own echo, see above. */
  private suppressNextChecklistsRebuild = false;

  getDataForParent() {
    const checklists = this.sectionForm.get('checklists')?.value;
    this.suppressNextChecklistsRebuild = true;
    this.callback.emit(checklists);
  }

  getChipTypeLabel(user: any): string {
    return ticketPeopleTypeLabel(user);
  }

  removeUsers(index: any, dataArray: any, checklistIndex: any) {
    dataArray.splice(index,1);
    this.updateChecklistAtBackend(checklistIndex);
  }

  /**
   * This checklist item's own observers and participants, flattened for the
   * collapsed-row avatar stack — each row now reflects its own assignment
   * instead of the ticket-wide People list.
   */
  checklistPeople(index: any) {
    // Keyed by type + id (a Vendor and a User can share an id) and named from
    // full_name, which the API resolves per type — attachable is the raw model.
    const toPerson = (person: any, role: string) => ({
      user: { id: ticketPeopleKey(person), full_name: person?.full_name },
      role,
    });
    const participants = (this.checklists().at(index)?.get('participants')?.value || []).map((p: any) => toPerson(p, 'Participant'));
    const observers = (this.checklists().at(index)?.get('observers')?.value || []).map((o: any) => toPerson(o, 'Observer'));
    return [...participants, ...observers];
  }

  getInitials(name: string): string {
    return (name || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || '')
      .join('');
  }

  /**
   * Colour by person, not by role.
   *
   * A role-based palette gave every participant on a row the same fill, so three
   * participants read as one indistinguishable blob. The role is still carried by
   * the tooltip, which is where it is actually legible.
   */
  private readonly AVATAR_COLORS: string[] = [
    '#ff6600', '#2563eb', '#14b8a6', '#8b5cf6', '#ec4899', '#f59e0b',
    '#10b981', '#6366f1', '#ef4444', '#0ea5e9', '#84cc16', '#d946ef',
  ];

  /**
   * Stable hash of the user id (name as fallback) so one person keeps the same
   * colour across rows, tabs and reloads rather than shifting with list order.
   */
  getAvatarColor(person: any): string {
    const key = String(person?.user?.id ?? person?.user?.full_name ?? '');
    if (!key) return '#6b7280';
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      hash = (Math.imul(31, hash) + key.charCodeAt(i)) | 0;
    }
    return this.AVATAR_COLORS[Math.abs(hash) % this.AVATAR_COLORS.length];
  }

  /** Set on toolbar mousedown so the following blur does not collapse the row. */
  private suppressBlurCollapse = false;

  onToolbarMouseDown() {
    // mousedown fires before blur. Without this flag the row would collapse to
    // view mode and unmount .bar-tools before its click handlers could run.
    this.suppressBlurCollapse = true;
  }

  onChecklistBlur(index: any) {
    if (this.isLocked) {
      return;
    }
    if (this.suppressBlurCollapse) {
      this.suppressBlurCollapse = false;
      return;
    }
    // Delegate so this component's existing empty-row semantics are preserved.
    this.ediViewChecklist(index, true);
  }

  ediViewChecklist(index: any,viewMode:boolean = false,editMode:boolean = false) {
    const text = this.checklists().at(index).get('text')?.value;
    /*if(!text) {
      this.checklists().removeAt(index);
      return;
    }*/
    if(editMode) {
      this.checklists().at(index).get('edit_mode')?.setValue(true);
      this.checklists().at(index).get('view_mode')?.setValue(false);
    }
    if(viewMode) {
      this.checklists().at(index).get('view_mode')?.setValue(true);
      this.checklists().at(index).get('edit_mode')?.setValue(false);
    }

    if(viewMode) {
      this.getDataForParent();
      this.updateChecklistAtBackend(index);
    }
  }

  onClickAddNewChecklist() {
    const id = this.getTicketId();
    if (!id) {
      this.toasterService.showWarning('Ticket Id not Found!');
      return;
    }
    this.addChecklist();
    this.saveChecklistAtBackend(this.checklists().length - 1)
  }

  saveChecklistAtBackend(index: any) {
    const ticketId = this.getTicketId();
    if (!ticketId) {
      this.toasterService.showWarning('Ticket Id not Found!');
      return;
    }
    const checklist = this.checklists().at(index)?.value;
    const data = {
      id: checklist?.id,
      text: checklist?.text,
      observers: checklist?.observers,
      participants: checklist?.participants,
      items: toChecklistItemRefs(checklist?.items),
      is_completed: checklist?.is_completed,
      is_important: checklist?.is_important,
      ticket_id: ticketId,
    }
    this.backendApiService.postApi('ticket/add/checklist',data)
      .subscribe((response: any) => {
        console.log('[CHECKLIST] New checklist created', response);
        this.checklists().at(index).get('id')?.setValue(response.data?.data?.id);
      })
  }

  updateChecklistAtBackend(index: any){
    // CRITICAL: Wait for Angular to update the FormControl value before emitting
    // This ensures the checkbox state is synchronized before progress calculation
    setTimeout(() => {
      this.getDataForParent();
      console.log('[CHECKLIST] Callback emitted after FormControl sync');
    }, 0);

    const ticketId = this.getTicketId();
    if (!ticketId) {
      console.log('[CHECKLIST] No ticket ID - skipping backend update');
      return;
    }

    const checklist = this.checklists().at(index)?.value;
    const data = {
      id: checklist?.id,
      text: checklist?.text,
      observers: checklist?.observers,
      participants: checklist?.participants,
      items: toChecklistItemRefs(checklist?.items),
      is_completed: checklist?.is_completed,
      is_important: checklist?.is_important,
    }
    this.backendApiService.patchApi(`ticket/update/checklist/${checklist?.id}`,data)
      .subscribe({
        next: (response: any) => {
          console.log('[CHECKLIST] Backend update successful', response);
        },
        error: () => this.toasterService.showError('Error', 'Failed to save checklist'),
      })
  }


}
